import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Clock, Check, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtCountdown = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Broadcaster-side widget for Private Shows.
 *
 * Lifecycle:
 *   idle (poll /pending every 5s)
 *     └── if pending request appears -> show Accept/Decline banner
 *   accepted (poll /status of active show every 3s)
 *     └── click End -> POST /end -> idle
 *     └── countdown 0 -> idle (server auto-expires)
 */
const PrivateShowCreatorWidget = ({ streamId, enabled = true }) => {
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState(null);
  const [respondingId, setRespondingId] = useState(null);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef(null);
  const statusPollRef = useRef(null);

  // Poll incoming requests every 5s while not in an active show
  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/private-shows/pending`, { withCredentials: true });
      const filtered = (res.data?.requests || []).filter(r => r.stream_id === streamId);
      setPending(filtered);
    } catch (e) { /* silent */ }
  }, [streamId]);

  useEffect(() => {
    if (!enabled || !streamId || active) return;
    fetchPending();
    pollRef.current = setInterval(fetchPending, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [enabled, streamId, active, fetchPending]);

  // While in an active show, poll its status every 3s
  useEffect(() => {
    if (!active) return;
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/api/private-shows/status/${active.id}`, { withCredentials: true });
        const s = res.data;
        if (s.status === 'accepted') {
          setActive(s);
        } else {
          // ended or expired
          toast(`Private show ${s.status}`);
          setActive(null);
        }
      } catch (e) { /* silent */ }
    };
    tick();
    statusPollRef.current = setInterval(tick, 3000);
    return () => { if (statusPollRef.current) clearInterval(statusPollRef.current); };
  }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Local ticker for countdown
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);

  const respond = async (showId, action) => {
    setRespondingId(showId);
    try {
      const res = await axios.post(
        `${API}/api/private-shows/respond/${showId}`,
        { action },
        { withCredentials: true },
      );
      if (action === 'accept') {
        setActive(res.data);
        setPending(prev => prev.filter(p => p.id !== showId));
        toast.success(`Private accepted — ${res.data.creator_share} tokens credited`);
      } else {
        setPending(prev => prev.filter(p => p.id !== showId));
        toast('Request declined');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setRespondingId(null);
    }
  };

  const endShow = async () => {
    if (!active) return;
    if (!window.confirm('End private show now?')) return;
    try {
      await axios.post(`${API}/api/private-shows/end/${active.id}`, {}, { withCredentials: true });
      setActive(null);
      toast.success('Private ended');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (!enabled || !streamId) return null;

  // ACTIVE SHOW (creator side)
  if (active) {
    const expMs = active.expires_at ? Date.parse(active.expires_at) : 0;
    const secs = Math.max(0, Math.floor((expMs - now) / 1000));
    return (
      <div className="bg-gradient-to-br from-indigo-900/70 to-blue-900/50 border-2 border-indigo-500/50 rounded-2xl p-5" data-testid="private-show-active-creator">
        <div className="flex items-center gap-2 mb-3">
          <Lock weight="fill" className="w-5 h-5 text-indigo-300 animate-pulse" />
          <h3 className="font-heading text-base font-bold text-white">IN PRIVATE</h3>
          <span className="ml-auto px-2 py-1 bg-black/40 rounded-lg flex items-center gap-1 text-indigo-200 font-mono text-sm">
            <Clock weight="fill" className="w-3.5 h-3.5" /> {fmtCountdown(secs)}
          </span>
        </div>
        <p className="text-xs text-indigo-200/90 mb-3">
          With <strong>{active.viewer_name}</strong> · {active.minutes} min · {active.creator_share} tok credited
        </p>
        <button onClick={endShow}
          className="w-full py-2 bg-red-500/30 text-red-100 rounded-lg text-sm font-semibold hover:bg-red-500/40 transition-colors"
          data-testid="creator-end-private-btn">
          End Private Show
        </button>
      </div>
    );
  }

  // PENDING REQUESTS
  if (!pending.length) return null;

  return (
    <AnimatePresence>
      {pending.map(req => (
        <motion.div
          key={req.id}
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, x: 50 }}
          className="bg-gradient-to-br from-amber-900/40 to-indigo-900/40 border-2 border-amber-500/50 rounded-2xl p-5"
          data-testid={`pending-private-${req.id}`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Lock weight="fill" className="w-5 h-5 text-amber-400 animate-pulse" />
            <h3 className="font-heading text-base font-bold text-white">Private Show Request</h3>
          </div>
          <div className="flex items-center gap-3 mb-3">
            {req.viewer_avatar && <img src={req.viewer_avatar} alt="" className="w-9 h-9 rounded-full object-cover" />}
            <div>
              <p className="text-white font-semibold text-sm">{req.viewer_name}</p>
              <p className="text-xs text-amber-200/80">
                {req.minutes} min × {req.price_per_minute} = <strong>{req.prepay_tokens} tokens</strong>
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => respond(req.id, 'accept')}
              disabled={respondingId === req.id}
              className="flex-1 py-2 bg-green-500 text-white rounded-lg text-sm font-bold hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid={`accept-private-${req.id}`}
            >
              <Check weight="bold" className="w-4 h-4" />
              {respondingId === req.id ? '…' : `Accept (+${Math.floor(req.prepay_tokens * 0.9)} tok)`}
            </button>
            <button
              onClick={() => respond(req.id, 'decline')}
              disabled={respondingId === req.id}
              className="flex-1 py-2 bg-slate-800 text-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid={`decline-private-${req.id}`}
            >
              <X weight="bold" className="w-4 h-4" /> Decline
            </button>
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  );
};

export default PrivateShowCreatorWidget;
